/** Shared admin form helpers - lightweight inputs and localized field editor. */
import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function Field({ label, children, hint }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.16em] font-semibold text-ink-500">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-ink-300 mt-1">{hint}</p>}
    </div>
  );
}

export function TextField({ label, value, onChange, hint, type = "text", testid, placeholder }) {
  return (
    <Field label={label} hint={hint}>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 h-10 bg-white border-ink-200/60 focus-visible:ring-corten-400"
      />
    </Field>
  );
}

export function LocalizedField({ label, value = {}, onChange, multiline = false, testid }) {
  const set = (lang, v) => onChange({ ...(value || {}), [lang]: v });
  const Ctrl = multiline ? Textarea : Input;
  return (
    <Field label={label}>
      <div className="mt-1 grid grid-cols-1 gap-2">
        {["it", "en", "de"].map((lng) => (
          <div key={lng} className="flex items-start gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink-300 mt-3 w-6">{lng}</span>
            <Ctrl
              value={value?.[lng] || ""}
              onChange={(e) => set(lng, e.target.value)}
              data-testid={`${testid}-${lng}`}
              className="flex-1 bg-white border-ink-200/60 focus-visible:ring-corten-400"
              rows={multiline ? 3 : undefined}
            />
          </div>
        ))}
      </div>
    </Field>
  );
}

export function Panel({ title, action, children, testid }) {
  return (
    <section className="rounded-2xl bg-white border border-ink-200/40 shadow-soft p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-editorial italic text-2xl">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function ActionButton({ children, onClick, variant = "primary", disabled, testid }) {
  const base = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100";
  const styles = {
    primary: "bg-corten-400 text-white hover:bg-corten-500",
    secondary: "bg-cream-100 text-ink-700 hover:bg-cream-200",
    danger: "bg-red-600 text-white hover:bg-red-700",
    ghost: "text-ink-500 hover:text-corten-500",
  };
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} className={`${base} ${styles[variant]}`}>
      {children}
    </button>
  );
}
